import { Link } from "react-router-dom";
import { ArrowRight, Quote } from "lucide-react";
import { Eyebrow, Page, PageHeader, Reveal } from "@/components/Reveal";
import { IMAGES, TESTIMONIALS } from "@/constants/site";

const CASES = [
  {
    tag: "Emergency Harvest",
    title: "Blueberry rush, beaten rain",
    copy: "A Fraser Valley grower called mid-season with fruit ripening fast and weather on the way. We scaled from a small picking crew to a full harvest team within 48 hours and cleared the fields before the storm.",
    image: IMAGES.farmField,
  },
  {
    tag: "Peak Season Support",
    title: "Cannery line, fully staffed",
    copy: "During the salmon run, a Greater Vancouver processor needed reliable line workers on rotating shifts. Barnala crews kept the line moving through the entire peak without a single unmanned station.",
    image: IMAGES.cannerySorting,
  },
  {
    tag: "Year-Round Partner",
    title: "Greenhouse crop cycles, handled",
    copy: "A Lower Mainland greenhouse operation trusts Barnala for picking, packing and sanitation crews across every crop cycle — the same familiar faces, season after season.",
    image: IMAGES.greenhouse,
  },
];

export default function Testimonials() {
  return (
    <Page>
      <PageHeader
        eyebrow="Testimonials"
        title={
          <>
            Trusted by growers <span className="italic text-[#86EFAC]">&amp; processors.</span>
          </>
        }
        copy="From family berry farms to busy cannery floors — here's what working with Barnala Labour Contracting looks like."
      />

      <section className="px-6 py-24 sm:px-10 lg:px-16">
        <div className="mx-auto max-w-6xl">
          <Reveal>
            <Eyebrow>In Their Words</Eyebrow>
          </Reveal>
          <div className="mt-12 grid gap-6 md:grid-cols-2">
            {TESTIMONIALS.map((t, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <figure
                  data-testid={`testimonial-card-${i}`}
                  className="flex h-full flex-col rounded-2xl border border-white/10 bg-[#163B2C]/40 p-8 transition-colors duration-500 hover:border-[#22C55E]/50"
                >
                  <Quote className="h-6 w-6 text-[#22C55E]" />
                  <blockquote className="mt-5 flex-1 font-display text-lg italic leading-relaxed text-[#F6F4EE]/85 sm:text-xl">
                    “{t.quote}”
                  </blockquote>
                  <figcaption className="mt-6 border-t border-white/10 pt-4">
                    <p className="text-sm font-semibold text-[#F6F4EE]">{t.name}</p>
                    <p className="mt-1 text-xs text-[#86EFAC]/80">{t.place}</p>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#0B1F17] px-6 py-24 sm:px-10 lg:px-16">
        <div className="mx-auto max-w-6xl">
          <Reveal>
            <Eyebrow>From The Field</Eyebrow>
          </Reveal>
          <div className="mt-12 space-y-16">
            {CASES.map((c, i) => (
              <Reveal key={c.title}>
                <article
                  data-testid={`case-study-${i}`}
                  className={`grid gap-10 lg:grid-cols-2 lg:items-center ${i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""}`}
                >
                  <div className="overflow-hidden rounded-2xl border border-white/10">
                    <img src={c.image} alt={c.title} className="aspect-[4/3] w-full object-cover" />
                  </div>
                  <div>
                    <span className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-[#22C55E]">
                      {c.tag}
                    </span>
                    <h3 className="mt-3 font-display text-2xl font-bold tracking-tight sm:text-3xl">{c.title}</h3>
                    <p className="mt-4 text-base leading-relaxed text-[#F6F4EE]/70">{c.copy}</p>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <div className="mt-16">
              <Link
                to="/contact"
                data-testid="testimonials-contact-link"
                className="group inline-flex items-center gap-2 text-sm font-semibold text-[#86EFAC]"
              >
                Become our next success story
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </Page>
  );
}
